import { useEffect, useState, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Admin } from "../../../api/client";
import { AuthContext } from "../../../components/AuthWrapper";
import ConfirmationModal from "../../../components/ConfirmModal";
import { DataTable } from "./DataTable";
import { createColumns } from "./columns";
import { Heading } from "../../../components/UiComponents/heading";
import PageTitle from "../../../components/PageTitle";
import { Container } from "../../../components/Container";
import { Button } from "../../../components/UiComponents/button";

export default function StaffComponent(): JSX.Element {
  const [data, setData] = useState<Admin[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [adminId, setAdminId] = useState<string>("");
  const [adminName, setAdminName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [netId, setNetId] = useState<string>("");

  const { client }: AuthContext = useOutletContext();

  const handleDelete = (id: string, name: string) => {
    setAdminId(id);
    setAdminName(name);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteAdmin = async (id: string) => {
    const toastForAdmins = toast.loading("Deleting admin");

    await client?.admin_DeleteAdmin(id)
      .then(() => {
        toast.success("Successfully deleted");
        getAdmins();
      })
      .catch((e) => {
        toast.error("An error occurred while deleting the admin.");
      })
      .finally(() => {
        toast.dismiss(toastForAdmins);
        setAdminId("");
        setAdminName("");
      });
  };

  const columns = useMemo(() => createColumns(handleDelete), []);

  const getAdmins = () => {
    setIsLoading(true);
    client?.admin_GetAdmins()
      .then((admins) => {
        if (admins === undefined || admins === null) {
          setData([]);
        } else {
          setData(admins);
        }
      })
      .catch((error) => {
        console.error("Error fetching admins:", error);
      })
      .finally(() => setIsLoading(false));
  };

  const handleAddMember = async (netId: string) => {
    const toastForAdding = toast.loading("Adding admin");
    await client?.admin_AddAdmin(netId)
      .then(() => {
        getAdmins();
        toast.success("Successfully added admin");
      })
      .catch((error) => {
        if (error.response && error.status === 400) {
          toast.error("This user is already an admin.", { duration: 3000 });
        } else {
          toast.error("An error occurred while adding the admin.");
        }
      })
      .finally(() => {
        setIsLoading(false);
        toast.dismiss(toastForAdding);
        setIsAddModalOpen(false);
        setNetId("");
      });
  };

  useEffect(() => {
    if (client) {
      getAdmins();
    }
  }, [client]);

  return (
    <Container>
      <PageTitle title="Manage Staff" />
      <Heading text="Manage Staff" />
      <div>
        <Button
          variant="default"
          className="mb-3"
          onClick={() => setIsAddModalOpen(true)}
          abbr="Click to add an admin using their USF NetID"
        >
          Add Staff Member
        </Button>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteAdmin}
        Id={adminId}
        TextMessage={`Are you sure you want to delete ${adminName} from admins?`}
        abbrCancel="Don't delete this admin"
        abbrConfirm="Delete this admin"
      />

      {/* Add Staff Member Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-40">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg">
            <h2 className="text-xl font-semibold mb-4 light:text-black dark:text-white">
              Add New Staff Member
            </h2>
            <div>
              <label className="block text-sm font-semibold light:text-black dark:text-white mb-2">
                NetID (the part of your USF email before "@usf.edu")
              </label>
              <input
                type="email"
                value={netId}
                onChange={(e) => setNetId(e.target.value)}
                className="border rounded w-full py-2 px-3 mb-4 dark:text-black"
                placeholder="rockythebull"
              />
            </div>
            <div className="flex justify-start gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddModalOpen(false);
                  setNetId("");
                }}
                abbr="Don't add this admin"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleAddMember(netId)}
                className="px-4 py-2 rounded bg-green-700 text-white hover:bg-green-800 transition-colors"
                abbr="Add this admin"
              >
                Add Staff Member
              </Button>
            </div>
          </div>
        </div>
      )}

      <div>
        <DataTable columns={columns} data={data} isLoading={isLoading} />
      </div>
    </Container>
  );
}
